import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { fetchAuthStatus, login as loginRequest, logout as logoutRequest } from "./auth";
import type { AuthStatus } from "./auth";
import { LoginPage } from "./LoginPage";
import { RouteFallback } from "./components/RouteFallback";

type AuthContextValue = {
  user: string | null;
  enabled: boolean;
  logout: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const value = useContext(AuthContext);
  if (!value) throw new Error("useAuth must be used inside AuthProvider");
  return value;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      setStatus(await fetchAuthStatus());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Unable to check sign in");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(async (username: string, password: string) => {
    setError(null);
    const next = await loginRequest(username, password);
    setStatus(next);
  }, []);

  const logout = useCallback(async () => {
    const next = await logoutRequest();
    setStatus(next);
  }, []);

  const contextValue = useMemo(
    () => ({
      user: status?.username ?? null,
      enabled: status?.enabled ?? false,
      logout,
    }),
    [logout, status?.enabled, status?.username],
  );

  if (loading) return <RouteFallback label="Checking sign in..." />;

  if (status?.enabled && !status.authenticated) {
    return <LoginPage error={error} onLogin={login} />;
  }

  return <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>;
}
